import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import { useAppDispatch } from "../redux";
import { markNotificationAsRead, deleteNotification } from "../redux/reducers/notificationSlice";
import { Notification } from "../redux/reducers/notificationSlice";
import { View, Text, Icon, Button } from "./base";

interface NotificationItemProps {
  notification: Notification;
  onAction?: () => void;
}

const CATEGORY_ICON: Record<string, string> = {
  account: "user",
  test: "check-circle",
  billing: "payments",
  support: "support_agent",
};

const CATEGORY_COLOR: Record<string, string> = {
  account: "text-purple-400",
  test: "text-yellow-400",
  billing: "text-pink-400",
  support: "text-blue-400",
};

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const NotificationItem = ({ notification, onAction }: NotificationItemProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isRead = !!notification.readAt;
  const categoryIcon = CATEGORY_ICON[notification.category] || "notification";
  const iconColor = isRead ? "text-gray-400 dark:text-gray-500" : CATEGORY_COLOR[notification.category] || "text-gray-500";

  const handleClick = async () => {
    if (!notification.readAt) {
      await dispatch(markNotificationAsRead(notification.id));
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    onAction?.();
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await dispatch(deleteNotification(notification.id));
  };

  return (
    <View
      role="button"
      onClick={handleClick}
      className={clsx(
        "flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group",
        "hover:bg-gray-100 dark:hover:bg-gray-700/50",
        !isRead && "bg-brand/5 dark:bg-brand/10",
      )}
    >
      <View className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center mt-0.5">
        <Icon
          name={categoryIcon}
          size={16}
          className={iconColor}
        />
      </View>

      <View className="flex-1 min-w-0">
        <Text className={clsx("text-sm font-medium truncate", isRead ? "text-gray-600 dark:text-gray-400" : "text-gray-900 dark:text-white")}>
          {notification.title}
        </Text>

        <Text className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{notification.message}</Text>

        {!isRead && notification.deletionPolicy === "delete_on_read" && (
          <Text className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 italic">Auto-deletes when read</Text>
        )}
      </View>

      <Text className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 self-center">{formatTimeAgo(notification.createdAt)}</Text>

      <View className="flex-shrink-0 flex items-center gap-1.5 self-center">
        {!isRead && <View className="w-2 h-2 rounded-full bg-brand dark:bg-cyan-400" />}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          outline
          onClick={handleDelete}
          className="!p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Delete notification"
        >
          <Icon
            name="trash"
            size={14}
            className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
          />
        </Button>
      </View>
    </View>
  );
};
